import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  description: "",
  category: "brigadeiros",
  image_url: "",
  active: true,
  variants: [{ id: "v1", label: "", pack_size: 50, weight_g: null, price: 0 }],
};

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        await api.put(`/admin/products/${data.id}`, data);
      } else {
        await api.post("/admin/products", data);
      }
      toast.success("Produto guardado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar este produto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Produto apagado");
    load();
  };

  return (
    <div data-testid="admin-products" className="p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-rose-accent">Catálogo</p>
          <h1 className="font-heading text-5xl text-cocoa-dark mt-2">Produtos</h1>
        </div>
        <button data-testid="new-product-button" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-cocoa-dark hover:bg-cocoa text-cream px-5 py-3 rounded-full">
          <Plus className="w-4 h-4" /> Novo produto
        </button>
      </div>

      <div className="bg-cream rounded-2xl border border-cocoa/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-deep text-cocoa-soft">
            <tr>
              <th className="text-left p-4 font-medium">Produto</th>
              <th className="text-left p-4 font-medium">Categoria</th>
              <th className="text-left p-4 font-medium">Variantes</th>
              <th className="text-left p-4 font-medium">Estado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-cocoa/10 hover:bg-cream-alt/40">
                <td className="p-4 flex items-center gap-3">
                  <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  <div>
                    <p className="text-cocoa-dark font-medium">{p.name}</p>
                    <p className="text-xs text-cocoa-soft line-clamp-1 max-w-xs">{p.description}</p>
                  </div>
                </td>
                <td className="p-4 text-cocoa-soft capitalize">{p.category}</td>
                <td className="p-4 text-cocoa-soft">{p.variants.length}</td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${p.active ? "bg-mint/30 text-cocoa-dark" : "bg-cocoa/10 text-cocoa-soft"}`}>
                    {p.active ? "Ativo" : "Inativo"}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="p-2 hover:bg-cream-alt rounded-full"><Pencil className="w-4 h-4 text-cocoa-soft" /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:bg-cream-alt rounded-full"><Trash2 className="w-4 h-4 text-rose-accent" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ProductForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ProductForm({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const input = "w-full bg-cream border border-cocoa/15 rounded-xl px-4 py-3 text-cocoa-dark placeholder:text-cocoa-soft/60 focus:outline-none focus:ring-2 focus:ring-rose focus:border-rose";

  const updateVariant = (i, k, v) => {
    const variants = [...form.variants];
    variants[i] = { ...variants[i], [k]: v };
    setForm({ ...form, variants });
  };
  const addVariant = () => setForm({ ...form, variants: [...form.variants, { id: `v${Date.now()}`, label: "", pack_size: 50, weight_g: null, price: 0 }] });
  const removeVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      variants: form.variants.map((v) => ({
        ...v,
        pack_size: parseInt(v.pack_size, 10),
        weight_g: v.weight_g ? parseInt(v.weight_g, 10) : null,
        price: parseFloat(v.price),
      })),
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-cocoa-dark/40 backdrop-blur-sm flex items-center justify-center p-6 overflow-y-auto">
      <form onSubmit={submit} className="bg-cream rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl text-cocoa-dark">{form.id ? "Editar produto" : "Novo produto"}</h2>
          <button type="button" onClick={onClose} className="p-2 hover:bg-cream-alt rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome" className={input} />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descrição" rows={3} className={input} />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
              <option value="salgados">Salgados</option>
              <option value="brigadeiros">Brigadeiros</option>
            </select>
            <label className="flex items-center gap-2 px-4 py-3 border border-cocoa/15 rounded-xl">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span className="text-sm text-cocoa-dark">Ativo</span>
            </label>
          </div>
          <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL da imagem" className={input} />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl text-cocoa-dark">Variantes (caixas)</h3>
              <button type="button" onClick={addVariant} className="text-xs uppercase tracking-widest text-rose-accent hover:underline">+ Adicionar</button>
            </div>
            <div className="space-y-2">
              {form.variants.map((v, i) => (
                <div key={v.id || `new-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <input value={v.label} onChange={(e) => updateVariant(i, "label", e.target.value)} placeholder="Etiqueta (ex: 12g · 50 un)" className="col-span-5 bg-cream-alt border border-cocoa/15 rounded-lg px-3 py-2 text-sm" />
                  <input type="number" value={v.pack_size} onChange={(e) => updateVariant(i, "pack_size", e.target.value)} placeholder="Qtd" className="col-span-2 bg-cream-alt border border-cocoa/15 rounded-lg px-3 py-2 text-sm" />
                  <input type="number" value={v.weight_g || ""} onChange={(e) => updateVariant(i, "weight_g", e.target.value)} placeholder="Peso(g)" className="col-span-2 bg-cream-alt border border-cocoa/15 rounded-lg px-3 py-2 text-sm" />
                  <input type="number" step="0.01" value={v.price} onChange={(e) => updateVariant(i, "price", e.target.value)} placeholder="€" className="col-span-2 bg-cream-alt border border-cocoa/15 rounded-lg px-3 py-2 text-sm" />
                  <button type="button" onClick={() => removeVariant(i)} className="col-span-1 text-rose-accent hover:bg-cream-alt p-2 rounded-full"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-full text-cocoa-soft hover:bg-cream-alt">Cancelar</button>
          <button data-testid="form-save" type="submit" className="px-6 py-2.5 rounded-full bg-cocoa-dark hover:bg-cocoa text-cream">Guardar</button>
        </div>
      </form>
    </div>
  );
}
